import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';

import { Box, Link, Stack, Typography } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { radius } from '@karehero/llama/theme/radius';

import StatusBar from '@/designs-dev/device/StatusBar';
import HomeIndicator from '@/designs-dev/device/HomeIndicator';
import ScreenRoot from '@/components/wrapped/ScreenRoot';
import ChatActionRow from '@/components/wrapped/ChatActionRow';
import ChatTyping from '@/components/wrapped/ChatTyping';
import ChatBubble from '@/karehero-app/tell-us-once/intake/ChatBubble';
import { PRE_SIGN_UP } from '@/karehero-app/auth/PreSignUp.content';

// Which screen you are on. Taps are not a way through: the dots say how much
// there is, and Get started is the way on.
const Dots = ({ count, active }: { count: number; active: number }) => (
  <Stack direction='row' spacing={0.75}>
    {Array.from({ length: count }).map((_, i) => (
      <Box
        key={i}
        sx={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          bgcolor: i === active ? 'primary.main' : 'custom.neutral.300',
        }}
      />
    ))}
  </Stack>
);

// A message landing. Small and quick: a bubble that slides across the screen is
// a bubble you watch instead of read.
//
// Full width with the bubble placed inside it, not wrapped around the bubble.
// As a shrink-to-fit box it became the flex item, which took the bubble's own
// alignment (every reply lined up on the left) and left its 82% max-width
// resolving against a box that was sizing itself to the text: "Welcome to
// KareHero" broke over two lines for no reason anybody could see.
const Arrive = ({
  children,
  align = 'start',
}: {
  children: ReactNode;
  align?: 'start' | 'end';
}) => (
  <Box
    sx={{
      width: '100%',
      display: 'flex',
      justifyContent: align === 'end' ? 'flex-end' : 'flex-start',
      '@keyframes chat-arrive': {
        from: { opacity: 0, transform: 'translateY(6px)' },
        to: { opacity: 1, transform: 'none' },
      },
      animation: 'chat-arrive 220ms ease-out',
    }}
  >
    {children}
  </Box>
);

// Asking instead of telling, from the first screen: hello, what we are, the
// question, and the four answers that would send somebody somewhere different.
// Tapping one is the way in, so the answer is the sign-up rather than a step
// before it.
//
// The greeting is a message rather than a heading, because that is what this
// screen is. Two bubbles, not one: hello and then the pitch, the way a person
// says it.
//
// The chat's own parts, not a copy of them: the bubble the intake uses and the
// teal rows every chat in the app answers with. If this becomes the opening, it
// is already the thing it turns into.
//
// No orb. It sat above the question taking a third of the screen and saying
// nothing the bubble does not.
// How far into a turn the chat is. A message at a time, and the dots while the
// answer is being written, because a chat that answers the instant you ask is a
// menu with bubbles on it.
type Turn = 'typing' | 'answer' | 'follow';

const HELLO_MS = 600;
const INTRO_MS = 900;
const ANSWER_MS = 950;
const FOLLOW_MS = 650;
const LAST_MS = 700;

const ChatScreen = ({
  asked,
  onAsk,
  onPick,
  lastAsked,
  animate,
  onReveal,
}: {
  /** Answers already given, in the order they were given. */
  asked: string[];
  onAsk: (id: string) => void;
  onPick: () => void;
  /** Something has landed, so the screen can put the question back on top. */
  onReveal?: () => void;
  /** The most recent question and its answer, for the screen to scroll to. */
  lastAsked?: RefObject<HTMLDivElement | null>;
  /**
   * Play the conversation out. Off for handover minis, which are the states as
   * drawn: a mini caught mid-sentence is a mini of nothing.
   */
  animate: boolean;
}) => {
  const { chat } = PRE_SIGN_UP;
  // Asked and answered, so it is not offered again. What is left is what "is
  // there anything else" is asking about.
  const left = chat.options.filter((o) => !asked.includes(o.id));

  // 0 hello, 1 hello and the dots, 2 the question and the answers under it.
  const [opening, setOpening] = useState(animate ? 0 : 2);
  useEffect(() => {
    if (opening >= 2) return;
    const t = window.setTimeout(
      () => setOpening(opening + 1),
      opening === 0 ? HELLO_MS : INTRO_MS,
    );
    return () => window.clearTimeout(t);
  }, [opening]);

  // Where the newest turn is up to. Restarted whenever another question is
  // asked, so each one gets its own pause.
  const [turn, setTurn] = useState<Turn>('follow');
  useEffect(() => {
    if (!asked.length) return;
    if (!animate) {
      setTurn('follow');
      return;
    }
    setTurn('typing');
    const answer = window.setTimeout(() => setTurn('answer'), ANSWER_MS);
    const follow = window.setTimeout(
      () => setTurn('follow'),
      ANSWER_MS + FOLLOW_MS,
    );
    return () => {
      window.clearTimeout(answer);
      window.clearTimeout(follow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asked.length]);

  const settled = opening >= 2 && turn === 'follow';

  // Never one answer on its own. A list of one is not a choice, so the chat
  // asks the last question itself rather than offering it: "is there anything
  // else" with a single row under it is the conversation pointing at the only
  // thing it has left to say.
  useEffect(() => {
    if (!animate || !settled || left.length !== 1) return;
    const t = window.setTimeout(() => onAsk(left[0].id), LAST_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled, left.length]);

  // Every arrival moves the transcript, and the screen scrolls on being told.
  // Without this the question was pinned to the top before the answer existed,
  // which meant a short transcript with nothing to scroll and a question left
  // wherever it landed.
  useEffect(() => {
    onReveal?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opening, turn, asked.length]);

  return (
    <Stack sx={{ width: '100%' }} spacing={1.5}>
      <Arrive>
        <ChatBubble from='keni' text={chat.greeting} />
      </Arrive>
      {opening === 1 && (
        <Arrive>
          <ChatTyping />
        </Arrive>
      )}
      {opening >= 2 && (
        <Arrive>
          <ChatBubble from='keni' text={chat.intro} />
        </Arrive>
      )}

      {asked.map((id, i) => {
        const reply = chat.replies[id];
        const label = chat.options.find((o) => o.id === id)?.label ?? '';
        // Only the newest turn is mid-flight; everything above it has landed.
        const live = i === asked.length - 1;
        return (
          <Box key={id} ref={live ? lastAsked : undefined}>
            <Stack spacing={1.5}>
              <Arrive align='end'>
                <ChatBubble from='user' text={label} />
              </Arrive>
              {live && turn === 'typing' && (
                <Arrive>
                  <ChatTyping />
                </Arrive>
              )}
              {reply && (!live || turn !== 'typing') && (
                // The list rides in the bubble it belongs to. ChatBubble keeps
                // newlines, so a few things we can help with is a few lines
                // rather than a second message about the first one.
                <Arrive>
                  <ChatBubble
                    from='keni'
                    text={`${reply.lead}\n${reply.list.map((l) => `· ${l}`).join('\n')}`}
                  />
                </Arrive>
              )}
              {/* Only under the newest answer, and only while something is
                  left to ask about. Asking "anything else" under the last
                  answer is a question with no answers under it, and the button
                  below is the way on by then. */}
              {reply && live && turn === 'follow' && left.length > 0 && (
                <Arrive>
                  <ChatBubble from='keni' text={chat.followUp} />
                </Arrive>
              )}
            </Stack>
          </Box>
        );
      })}

      {/* The answers arrive with the question, and go while one is being
          answered: a list you can tap during the dots is a chat you can
          interrupt. */}
      {settled && left.length > 0 && (
        <Arrive>
          <Stack sx={{ width: '100%', pt: 1 }} spacing={1.5}>
            {left.map((option) => (
              <ChatActionRow
                key={option.id}
                label={option.label}
                // Written answers are answered in the chat. The rest are still
                // the way in, so tapping one signs up rather than doing
                // nothing.
                onClick={() =>
                  chat.replies[option.id] ? onAsk(option.id) : onPick()
                }
              />
            ))}
          </Stack>
        </Arrive>
      )}
    </Stack>
  );
};

/**
 * Which shape of this is being tried.
 *
 * `carousel` tells them what KareHero is over four slides. `chat` says hello and
 * then asks: the welcome screen, and the chat behind it.
 *
 * Named for what they are rather than numbered, because which one is option one
 * is a decision about the page, and that has already changed once.
 */
export type PreSignUpOption = 'carousel' | 'chat';

type Screen = { story?: (typeof PRE_SIGN_UP.slides)[number]; chat?: boolean };

const sequence = (option: PreSignUpOption): Screen[] =>
  option === 'carousel'
    ? PRE_SIGN_UP.slides.map((story) => ({ story }))
    : [{ story: PRE_SIGN_UP.slides[0] }, { chat: true }];

// The way in and the story, on one screen. It opens on Welcome to KareHero and
// pages through what the app is.
//
// Both actions sit at the bottom of every slide, in the same place: the way in is
// never a slide away, so somebody who arrived from a benefits letter and knows
// why they are here does not have to page through to get on with it, and nothing
// moves under their thumb as they do page through.
//
// Two of them, not three. Get started and Create account were the same button
// twice, so Get started is the one that stayed and signing in is a small line
// under it, which is the shape of who arrives here: mostly people who have not
// been before.
const PreSignUp = ({
  onCreateAccount,
  onSignIn,
  option = 'carousel',
  initialSlide = 0,
  initialAsked,
  animate = true,
}: {
  onCreateAccount: () => void;
  onSignIn: () => void;
  /** Which shape of the opening. See PreSignUpOption. */
  option?: PreSignUpOption;
  /** Jump-state for stories and handover minis. */
  initialSlide?: number;
  /** Answers already given in the chat, for the same reason. */
  initialAsked?: string[];
  /** Play the conversation out. Off for handover minis. See ChatScreen. */
  animate?: boolean;
}) => {
  const [slide, setSlide] = useState(initialSlide);
  const [asked, setAsked] = useState<string[]>(initialAsked ?? []);
  const screens = sequence(option);
  const total = screens.length;
  const current = screens[slide] ?? screens[0];
  const chat = Boolean(current.chat);
  const last = slide === total - 1;
  const story = current.story;

  // A chat starts at the top, which is what "Welcome to KareHero" halfway down
  // the screen was wrong about.
  //
  // Once something has been asked, the question goes to the top instead, so the
  // answer to it reads from there rather than from wherever the greeting left
  // off. Measured rather than computed from offsets, since the transcript is not
  // the offset parent, and the container scrolls rather than the message:
  // scrollIntoView would take the flows page with it.
  const transcript = useRef<HTMLDivElement>(null);
  const lastAsked = useRef<HTMLDivElement>(null);
  const [reveals, setReveals] = useState(0);
  useEffect(() => {
    const el = transcript.current;
    if (!el) return;
    const block = lastAsked.current;
    if (!block) {
      el.scrollTop = 0;
      return;
    }
    // Flush, not nearly: any gap above it is filled by the message before it,
    // and the question is meant to be the top of the screen.
    el.scrollTop +=
      block.getBoundingClientRect().top - el.getBoundingClientRect().top;
  }, [asked.length, reveals]);

  return (
    <ScreenRoot>
      <StatusBar />

      <Box
        ref={transcript}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: chat ? 'flex-start' : 'center',
          pt: chat ? 3 : 0,
          pb: chat ? 2 : 0,
          // The chat reads left, like every other chat in the app. Only the
          // story slides are centred.
          textAlign: chat ? 'left' : 'center',
          px: 3,
        }}
      >
        {chat || !story ? (
          <ChatScreen
            asked={asked}
            onAsk={(id) => setAsked([...asked, id])}
            onPick={onCreateAccount}
            lastAsked={lastAsked}
            animate={animate}
            onReveal={() => setReveals((n) => n + 1)}
          />
        ) : (
          <>
            {/* The illustration, above the words on every slide, at the same
                height whether it is drawn yet or not, so nothing moves between
                slides.

                A slide without one gets a dashed slot rather than a stand-in,
                the way PlaceholderScreen marks a screen nobody has drawn: a
                neutral block read as a rendering bug. */}
            {story.image ? (
              <Box
                sx={{
                  width: '100%',
                  height: 200,
                  mb: 5,
                  objectFit: 'cover',
                  borderRadius: `${radius.md}px`,
                }}
                component='img'
                src={story.image}
                alt=''
              />
            ) : (
              <Stack
                sx={{
                  width: '100%',
                  height: 200,
                  mb: 5,
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 1,
                  borderStyle: 'dashed',
                  borderColor: 'custom.neutral.500',
                  borderRadius: `${radius.md}px`,
                }}
              >
                <Typography sx={{ color: 'text.secondary' }} variant='overline'>
                  Illustration
                </Typography>
              </Stack>
            )}
            {/* display1: the serif is the brand voice, and this is the first thing
            anybody reads. */}
            <Typography
              sx={{ mb: 1.5, color: 'custom.marineGreen.500' }}
              variant='display1'
            >
              {story.title}
            </Typography>
            <Typography variant='body1' color='text.secondary'>
              {story.body}
            </Typography>
          </>
        )}
      </Box>

      {/* Fixed at the bottom, the same on every slide. Get started walks the
          story; on the last slide there is no story left, so it does what the
          screen is for. */}
      <Stack sx={{ px: 3, alignItems: 'center' }} spacing={2}>
        {/* Only the carousel gets dots. Hello and then the chat is two
            screens rather than a set of slides, and dots over it promised a
            set of them. */}
        {option === 'carousel' && total > 1 && (
          <Dots count={total} active={slide} />
        )}
        {/* Get started while there is a screen after this one, and Create
            account on the last, where that is the only thing left to do. It
            used to say Get started on both, which on the last screen was a
            button naming the thing before the one it did. */}
        <Button
          variant='primary'
          size='lg'
          isFullWidth
          ariaLabel={last ? PRE_SIGN_UP.createAccount : PRE_SIGN_UP.getStarted}
          onPress={() => (last ? onCreateAccount() : setSlide(slide + 1))}
        >
          {last ? PRE_SIGN_UP.createAccount : PRE_SIGN_UP.getStarted}
        </Button>
        <Link
          component='button'
          type='button'
          variant='body2'
          onClick={onSignIn}
          sx={{ mt: 0.5 }}
        >
          {PRE_SIGN_UP.signIn}
        </Link>
      </Stack>

      <HomeIndicator />
    </ScreenRoot>
  );
};

export default PreSignUp;
